import { AwsRum, type AwsRumConfig } from 'aws-rum-web';

const APPLICATION_ID = process.env.REACT_APP_RUM_APPLICATION_ID || '';
const APPLICATION_VERSION = '1.0.0';
const APPLICATION_REGION = process.env.REACT_APP_RUM_REGION || 'eu-west-1';

const rumConfig: AwsRumConfig = {
  sessionSampleRate: Number(process.env.REACT_APP_RUM_SESSION_SAMPLE_RATE) ?? 1,
  identityPoolId: process.env.REACT_APP_RUM_IDENTITY_POOL_ID || '',
  endpoint: process.env.REACT_APP_RUM_ENDPOINT || '',
  telemetries: ['performance', 'errors', 'http'],
  allowCookies: process.env.REACT_APP_RUM_ALLOW_COOKIES === 'true',
  enableXRay: process.env.REACT_APP_RUM_ENABLE_XRAY === 'true',
  signing: process.env.REACT_APP_RUM_SIGNING === 'true',
};

let awsRum: AwsRum | null = null;

export const initializeRUM = (): AwsRum | null => {
  if (awsRum) {
    return awsRum;
  }

  if (!APPLICATION_ID || !rumConfig.identityPoolId || !rumConfig.endpoint) {
    console.warn(
      'AWS RUM not initialized: Missing required environment variables'
    );
    return null;
  }

  try {
    awsRum = new AwsRum(
      APPLICATION_ID,
      APPLICATION_VERSION,
      APPLICATION_REGION,
      rumConfig
    );

    console.log('AWS RUM initialized successfully');
    return awsRum;
  } catch (error) {
    console.warn('Failed to initialize AWS RUM:', error);
    return null;
  }
};

export const getRUM = (): AwsRum | null => {
  return awsRum;
};

export const trackFilterChange = (
  filterType: string,
  oldValue: any,
  newValue: any,
  details?: Record<string, any>
): void => {
  const rum = getRUM();
  if (rum) {
    rum.recordEvent('filter_change', {
      filterType,
      oldValue,
      newValue,
      timestamp: Date.now(),
      ...details,
    });
  }
};

export const trackApiCall = (
  endpoint: string,
  method: string,
  duration: number,
  status: number,
  success: boolean
): void => {
  const rum = getRUM();
  if (rum) {
    rum.recordEvent('api_call', {
      endpoint,
      method,
      duration,
      status,
      success,
      timestamp: Date.now(),
    });
  }
};

export const trackAuthEvent = (
  event: 'login_attempt' | 'login_success' | 'login_failure' | 'logout',
  details?: Record<string, any>
): void => {
  const rum = getRUM();
  if (rum) {
    rum.recordEvent('auth_event', {
      event,
      timestamp: Date.now(),
      ...details,
    });
  }
};

export const trackExportEvent = (
  exportType: 'excel',
  year: number,
  projectCount: number,
  visibleColumns: string[],
  details?: Record<string, any>
): void => {
  const rum = getRUM();
  if (rum) {
    rum.recordEvent('export_event', {
      exportType,
      year,
      projectCount,
      visibleColumnsCount: visibleColumns.length,
      visibleColumns,
      timestamp: Date.now(),
      ...details,
    });
  }
};

export const trackColumnConfigEvent = (
  action:
    | 'open_modal'
    | 'save_changes'
    | 'reset_default'
    | 'select_all'
    | 'deselect_all',
  visibleColumns: string[],
  totalColumns: number,
  details?: Record<string, any>
): void => {
  const rum = getRUM();
  if (rum) {
    rum.recordEvent('column_config_event', {
      action,
      visibleColumnsCount: visibleColumns.length,
      totalColumns,
      visibleColumns,
      timestamp: Date.now(),
      ...details,
    });
  }
};

export const trackProjectViewEvent = (
  projectId: string,
  projectName: string,
  buildingType?: string,
  details?: Record<string, any>
): void => {
  const rum = getRUM();
  if (rum) {
    rum.recordEvent('project_view_event', {
      projectId,
      projectName,
      buildingType,
      timestamp: Date.now(),
      ...details,
    });
  }
};

export const trackError = (
  error: Error,
  context?: string,
  details?: Record<string, any>
): void => {
  const rum = getRUM();
  if (rum) {
    rum.recordError({
      name: error.name,
      message: error.message,
      stack: error.stack || '',
      context: context || 'unknown',
      ...details,
    });
  }
};

export const sanitizeErrorDetails = (details: any) => {
  const sanitized = { ...details };
  if (sanitized.componentStack) {
    sanitized.componentStack = sanitized.componentStack
      .split('\n')
      .map((line: string) => line.split(' ')[0])
      .join('\n');
  }
  return sanitized;
};

const rumService = {
  initializeRUM,
  getRUM,
  trackFilterChange,
  trackApiCall,
  trackAuthEvent,
  trackExportEvent,
  trackColumnConfigEvent,
  trackProjectViewEvent,
  trackError,
  sanitizeErrorDetails,
};

export default rumService;
